import React from 'react';
import {
  backgroundColorOptions,
  textColorOptions,
  accentColorOptions
} from './theme/colors';

const StatsComponent = ({
  title = 'Trusted by teams who move fast',
  description = 'Numbers that highlight the impact of your product or service.',
  alignment = 'center',
  columns = 3,
  backgroundColor = 'white',
  textColor = 'slate-900',
  accentColor = 'blue',
  variant = 'plain',
  stats = [
    { value: '4x', label: 'Faster launches', subtext: 'when teams adopt Puck' },
    { value: '95%', label: 'Editor adoption', subtext: 'within the first month' },
    { value: '120+', label: 'Enterprise teams', subtext: 'building content experiences' }
  ]
}) => {
  // Removed color resolution - now using Tailwind dark mode classes
  const accent = { textClass: 'text-blue-600' }; // Keep accent for styling

  const alignmentClasses = {
    left: 'text-left items-start',
    center: 'text-center items-center',
    right: 'text-right items-end'
  };

  const gridCols = () => {
    const safeColumns = Math.max(1, Math.min(columns, 4));
    if (safeColumns === 1) return 'grid-cols-1';
    if (safeColumns === 2) return 'grid-cols-1 md:grid-cols-2';
    if (safeColumns === 3) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
  };

  const statWrapper = () => {
    if (variant === 'cards') {
      return 'bg-white/90 dark:bg-slate-900/50 border border-slate-200/70 dark:border-white/10 rounded-2xl p-8 shadow-xs transition-colors duration-300';
    }
    if (variant === 'outlined') {
      return 'border border-slate-200 dark:border-white/10 rounded-xl p-6';
    }
    return 'p-0';
  };

  return (
    <section className="w-full bg-white dark:bg-slate-900">
      <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <div className={`flex flex-col gap-6 mb-12 ${alignmentClasses[alignment] || alignmentClasses.center}`}>
          {title && (
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </h2>
          )}
          {description && (
            <p className={`text-lg text-slate-600 dark:text-slate-300 max-w-3xl ${alignment === 'center' ? 'mx-auto' : ''}`}>
              {description}
            </p>
          )}
        </div>

        <div className={`grid ${gridCols()} gap-8`}>
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`flex flex-col gap-3 ${statWrapper()} ${alignment === 'center' ? 'items-center text-center' : ''}`}
            >
              <span className={`text-4xl md:text-5xl font-semibold tracking-tight ${accent.textClass}`} style={accent.style}>
                {stat.value}
              </span>
              <span className="text-lg font-medium text-slate-900 dark:text-slate-100">
                {stat.label}
              </span>
              {stat.subtext && (
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
                  {stat.subtext}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Stats = {
  fields: {
    title: {
      type: 'text',
      label: 'Titel'
    },
    description: {
      type: 'textarea',
      label: 'Beschreibung'
    },
    alignment: {
      type: 'select',
      label: 'Ausrichtung',
      options: [
        { label: 'Links', value: 'left' },
        { label: 'Zentriert', value: 'center' },
        { label: 'Rechts', value: 'right' }
      ]
    },
    columns: {
      type: 'select',
      label: 'Anzahl Spalten',
      options: [
        { label: '1', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 }
      ]
    },
    backgroundColor: {
      type: 'select',
      label: 'Hintergrundfarbe',
      options: backgroundColorOptions()
    },
    textColor: {
      type: 'select',
      label: 'Textfarbe',
      options: textColorOptions()
    },
    accentColor: {
      type: 'select',
      label: 'Akzentfarbe',
      options: accentColorOptions()
    },
    variant: {
      type: 'select',
      label: 'Layout',
      options: [
        { label: 'Schlicht', value: 'plain' },
        { label: 'Karten', value: 'cards' },
        { label: 'Umrandet', value: 'outlined' }
      ]
    },
    stats: {
      type: 'array',
      label: 'Statistiken',
      arrayFields: {
        value: {
          type: 'text',
          label: 'Wert'
        },
        label: {
          type: 'text',
          label: 'Beschreibung'
        },
        subtext: {
          type: 'textarea',
          label: 'Untertitel'
        }
      },
      getItemSummary: (item, index) => item?.label || `Stat ${index + 1}`
    }
  },
  defaultProps: {
    title: 'Trusted by teams who move fast',
    description: 'Numbers that highlight the impact of your product or service.',
    alignment: 'center',
    columns: 3,
    backgroundColor: 'white',
    textColor: 'slate-900',
    accentColor: 'blue',
    variant: 'plain',
    stats: [
      { value: '4x', label: 'Faster launches', subtext: 'when teams adopt Puck' },
      { value: '95%', label: 'Editor adoption', subtext: 'within the first month' },
      { value: '120+', label: 'Enterprise teams', subtext: 'building content experiences' }
    ]
  },
  render: StatsComponent
};
